import { useState, useRef, useEffect, useCallback, type CSSProperties } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { User, LogOut, LogIn, ChevronDown, Menu, X, ClipboardList, UserPlus } from "lucide-react";
import { useApp } from "../context/AppContext";
import { buildUrlWithParams } from "../utils/queryParamStore";
import { isGuestSession, isRegisteredSession } from "../lib/authSession";
import { markLoggedOut } from "../lib/loggedOutFlag";
import { BrandLogo } from "./BrandLogo";
import NotificationBell from "./notifications/NotificationBell";

/** Primary marketing nav — Studio-first (Mock 1 re-layout).
 *  "Studio" → /studio (Mock 3 landing) and "Upskill Plan" → /upskill-plan
 *  (Mock 4 landing). */
const primaryNavLinks = [
  { path: "/studio", label: "Studio" },
  { path: "/upskill-plan", label: "Upskill Plan" },
  { path: "/how-it-works", label: "How It Works" },
  { path: "/learn", label: "Learn" },
  { path: "/blog", label: "Blogs" },
];

/** Secondary links shown in the mobile drawer only */
const moreNavLinks = [
  { path: "/pricing", label: "Pricing" },
  { path: "/faq", label: "FAQ" },
  { path: "/about", label: "About" },
  { path: "/contact", label: "Contact" },
];

const menuItemStyle: CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "12px 16px",
  background: "transparent",
  border: "none",
  fontSize: "14px",
  cursor: "pointer",
  textAlign: "left",
  textDecoration: "none",
};

export default function Header() {
  const location = useLocation();
  const { isLoggedIn, user, logout, isPaid, userTier, openLoginModal, sessionConfirmed } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    markLoggedOut();
    window.location.href = "/";
  };

  const pathname = location.pathname.replace(/\/$/, "") || "/";

  const isActivePath = (path: string) => {
    if (path === "/blog") {
      return pathname === "/blog" || pathname.startsWith("/blog/");
    }
    if (path === "/learn") {
      return pathname === "/learn" || pathname.startsWith("/learn/");
    }
    return pathname === path;
  };

  const isGuest = isGuestSession(user, userTier, isLoggedIn);
  const isRegisteredUser = isRegisteredSession(user, userTier, isLoggedIn);
  const showAccountMenu = sessionConfirmed && isRegisteredUser;

  const handleGuestSaveProgress = useCallback(() => {
    setMenuOpen(false);
    openLoginModal("Create an account to save your progress", undefined, "signup");
  }, [openLoginModal]);

  const filteredMoreLinks = moreNavLinks.filter((link) => !(isPaid && link.path === "/pricing"));
  const desktopNavLinks = [
    ...primaryNavLinks,
    ...(!isPaid ? ([{ path: "/pricing", label: "Pricing" }] as const) : []),
  ];

  const desktopNavLinkClass = (path: string) =>
    `header-desktop-nav-link${isActivePath(path) ? " header-desktop-nav-link--active" : ""}`;

  // Prefer the account name; if the stored name is an email address (some
  // accounts default name to the signup email), show only the local part so the
  // nav never renders a full email.
  const rawName = (user?.name ?? "").trim();
  const cleanName = rawName.includes("@") ? (rawName.split("@")[0] ?? rawName) : rawName;
  const displayName = isGuest ? (cleanName || "Guest") : (cleanName || "User");

  return (
    <header
      style={{
        background: "var(--navy-deep)",
        borderBottom: "none",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <div
        className="container"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 24px",
        }}
      >
        <Link
          to="/"
          aria-label="AI Fluens — Home"
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            background: "transparent",
            lineHeight: 0,
          }}
        >
          <BrandLogo height={56} priority />
        </Link>

        <nav
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
          className="desktop-nav"
          aria-label="Primary"
        >
          {desktopNavLinks.map((link) => (
            <NavLink
              key={link.path}
              to={buildUrlWithParams(link.path)}
              end={link.path !== "/blog" && link.path !== "/learn"}
              className={desktopNavLinkClass(link.path)}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-menu-toggle"
            style={{
              display: "none",
              padding: "8px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "8px",
              color: "rgba(248, 250, 252, 0.85)",
              cursor: "pointer",
            }}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {showAccountMenu ? (
            <>
              <NotificationBell />
            <div ref={menuRef} style={{ position: "relative" }}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="User menu"
                aria-expanded={menuOpen}
                aria-haspopup="true"
                data-testid={isGuest ? "guest-account-menu-button" : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  background: "rgba(255,255,255,0.08)",
                  border: isGuest ? "1px solid rgba(212, 175, 98, 0.35)" : "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "8px",
                  color: "rgba(248, 250, 252, 0.9)",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                <User size={18} style={isGuest ? { color: "#d4af37" } : undefined} />
                <span className="user-name">
                  {displayName}
                  {isGuest && (
                    <span
                      style={{
                        marginLeft: "6px",
                        padding: "2px 7px",
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.4px",
                        color: "#d4af37",
                        background: "rgba(212, 175, 98, 0.15)",
                        border: "1px solid rgba(212, 175, 98, 0.35)",
                        borderRadius: "9999px",
                        lineHeight: "16px",
                        verticalAlign: "middle",
                      }}
                    >
                      GUEST
                    </span>
                  )}
                  {isRegisteredUser && isPaid && (
                    <span
                      style={{
                        marginLeft: "6px",
                        padding: "2px 7px",
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        color: "#fff",
                        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                        borderRadius: "9999px",
                        lineHeight: "16px",
                        verticalAlign: "middle",
                      }}
                    >
                      PRO
                    </span>
                  )}
                </span>
                <ChevronDown
                  size={16}
                  style={{
                    transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: "8px",
                    minWidth: "200px",
                    background: "#0a1628",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
                    overflow: "hidden",
                    zIndex: 200,
                  }}
                >
                  {isGuest ? (
                    <>
                      <div
                        style={{
                          padding: "12px 16px 10px",
                          fontSize: "13px",
                          color: "rgba(212, 175, 98, 0.85)",
                          borderBottom: "1px solid rgba(255,255,255,0.07)",
                        }}
                      >
                        Building as guest
                      </div>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleGuestSaveProgress}
                        style={{
                          ...menuItemStyle,
                          color: "#d4af37",
                          fontWeight: 600,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <UserPlus size={16} />
                        Save your progress
                      </button>
                      <div style={{ height: "1px", background: "var(--border)" }} />
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          void handleLogout();
                        }}
                        aria-label="Logout"
                        style={{ ...menuItemStyle, color: "#ef4444" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to={buildUrlWithParams("/profile")}
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        style={{ ...menuItemStyle, color: "var(--text-secondary)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <User size={16} />
                        View Profile
                      </Link>
                      <Link
                        to={buildUrlWithParams("/my-plans")}
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        style={{ ...menuItemStyle, color: "var(--text-secondary)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <ClipboardList size={16} />
                        My Plans
                      </Link>
                      {!isPaid && (
                        <Link
                          to={buildUrlWithParams("/pricing")}
                          role="menuitem"
                          onClick={() => setMenuOpen(false)}
                          style={{ ...menuItemStyle, color: "var(--primary-light)", fontWeight: 600 }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          Purchase Plan
                        </Link>
                      )}
                      <div style={{ height: "1px", background: "var(--border)" }} />
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          void handleLogout();
                        }}
                        aria-label="Logout"
                        style={{ ...menuItemStyle, color: "#ef4444" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }} className="header-auth-actions">
              <button
                type="button"
                data-testid="header-login-button"
                onClick={() => openLoginModal()}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: "8px 10px",
                  color: "rgba(248, 250, 252, 0.85)",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Sign in
              </button>
              <button
                type="button"
                data-testid="header-signup-button"
                className="header-signup-hide-narrow"
                onClick={() => openLoginModal(undefined, undefined, "signup")}
                style={{
                  background: "rgba(0, 0, 0, 0.25)",
                  border: "1px solid rgba(255, 255, 255, 0.85)",
                  borderRadius: "8px",
                  padding: "9px 18px",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <LogIn size={16} />
                Sign up
              </button>
            </div>
          )}
        </div>
      </div>

      <nav
        className="mobile-nav"
        aria-label="Mobile navigation"
        style={{
          display: mobileMenuOpen ? "flex" : "none",
          flexDirection: "column",
          background: "#071020",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          padding: "8px 0",
        }}
      >
        {[...primaryNavLinks, ...filteredMoreLinks].map((link) => (
          <Link
            key={link.path}
            to={buildUrlWithParams(link.path)}
            aria-current={isActivePath(link.path) ? "page" : undefined}
            style={{
              padding: "14px 24px",
              background: isActivePath(link.path) ? "rgba(20, 184, 166, 0.12)" : "transparent",
              border: "none",
              borderLeft: isActivePath(link.path) ? "3px solid var(--primary-light)" : "3px solid transparent",
              color: isActivePath(link.path) ? "var(--primary-light)" : "rgba(248, 250, 252, 0.8)",
              fontSize: "15px",
              fontWeight: isActivePath(link.path) ? 600 : 400,
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.2s",
              textDecoration: "none",
              display: "block",
            }}
          >
            {link.label}
          </Link>
        ))}
        {!showAccountMenu && (
          <div
            style={{
              borderTop: "1px solid rgba(255,255,255,0.08)",
              marginTop: "8px",
              paddingTop: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
            className="mobile-auth-actions"
          >
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                openLoginModal();
              }}
              style={{
                margin: "0 24px",
                padding: "12px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Sign in
            </button>
            <button
              type="button"
              className="header-signup-hide-narrow"
              onClick={() => {
                setMobileMenuOpen(false);
                openLoginModal(undefined, undefined, "signup");
              }}
              style={{
                margin: "0 24px 12px",
                padding: "12px",
                background: "rgba(0,0,0,0.25)",
                border: "1px solid rgba(255,255,255,0.85)",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Sign up
            </button>
          </div>
        )}
      </nav>

      <style>{`
        .header-desktop-nav-link {
          display: inline-block;
          padding: 8px 14px;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 8px;
          color: rgba(248, 250, 252, 0.75);
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: color 0.2s, background 0.2s, border-color 0.2s, box-shadow 0.2s;
          position: relative;
          text-decoration: none;
          box-shadow: none;
        }
        .header-desktop-nav-link--active {
          color: #fff;
          font-weight: 600;
          background: rgba(255, 255, 255, 0.07);
          border-color: rgba(45, 212, 191, 0.4);
          box-shadow:
            0 1px 2px rgba(0, 0, 0, 0.22),
            0 3px 10px rgba(0, 0, 0, 0.18),
            inset 0 1px 0 rgba(255, 255, 255, 0.09);
        }
        .header-desktop-nav-link--active:hover {
          background: rgba(255, 255, 255, 0.09);
          border-color: rgba(45, 212, 191, 0.55);
          box-shadow:
            0 2px 4px rgba(0, 0, 0, 0.25),
            0 6px 14px rgba(0, 0, 0, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }
        .header-desktop-nav-link:not(.header-desktop-nav-link--active):hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(255, 255, 255, 0.14);
          box-shadow:
            0 1px 2px rgba(0, 0, 0, 0.2),
            0 2px 6px rgba(0, 0, 0, 0.12),
            inset 0 1px 0 rgba(255, 255, 255, 0.06);
        }
        @media (min-width: 768px) {
          .desktop-nav {
            display: flex !important;
          }
          .mobile-menu-toggle {
            display: none !important;
          }
          .mobile-nav {
            display: none !important;
          }
        }
        @media (max-width: 767px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-toggle {
            display: flex !important;
          }
          .user-name {
            display: none;
          }
          .header-signup-hide-narrow {
            display: none !important;
          }
        }
      `}</style>
    </header>
  );
}
